import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import { toast } from "react-toastify";
import {
  FaLock,
  FaExclamationTriangle,
  FaCheck,
  FaWallet,
  FaUser,
  FaLink,
  FaDollarSign,
  FaInfoCircle,
  FaCopy,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaStar,
  FaFire,
  FaMoneyBillWave,
  FaTrophy,
  FaUsers,
  FaChevronRight,
  FaShieldAlt,
} from "react-icons/fa";

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollingIntervalRef = useRef(null);
  const previousBalanceRef = useRef(null);

  useEffect(() => {
    fetchDashboard();
    pollingIntervalRef.current = setInterval(() => {
      checkForBalanceUpdates();
    }, 20000);
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkForBalanceUpdates = async () => {
    try {
      await refreshUser();
      const response = await api.get("/users/dashboard");
      const newDashboard = response.data.dashboard;
      if (
        previousBalanceRef.current !== null &&
        previousBalanceRef.current !== newDashboard.balance
      ) {
        const diff = newDashboard.balance - previousBalanceRef.current;
        if (diff > 0) {
          toast.success(`+$${diff.toFixed(2)} received`, {
            autoClose: 4000,
          });
        }
      }
      previousBalanceRef.current = newDashboard.balance;
      setDashboard(newDashboard);
    } catch (error) {
      console.error("Failed to check balance updates");
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/users/dashboard");
      const dashboardData = response.data.dashboard;
      setDashboard(dashboardData);
      previousBalanceRef.current = dashboardData.balance;
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <div className="w-12 h-12 rounded-full border-[3px] border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <p className="text-sm text-slate-400 animate-pulse">Loading...</p>
      </div>
    );
  }

  /* ─── NOT ACTIVATED ─── */
  if (!user?.isActivated) {
    return (
      <div className="space-y-5 pb-6">
        {/* Compact lock header */}
        <div className="text-center pt-2 pb-4">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 flex items-center justify-center mb-3 shadow-lg">
            <FaLock className="text-2xl text-slate-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Account Locked</h1>
          <p className="text-slate-400 text-sm mt-1">
            Activate to unlock all features
          </p>
        </div>

        {/* Activation CTA */}
        <div className="rounded-2xl border border-amber-400/20 bg-gradient-to-b from-amber-500/5 to-transparent p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-400/15 flex items-center justify-center flex-shrink-0">
              <FaExclamationTriangle className="text-amber-300" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">
                Activation Required
              </h2>
              <p className="text-sm text-slate-400 mt-0.5">
                One-time{" "}
                <span className="text-amber-200 font-semibold">$50 USDT</span>{" "}
                payment
              </p>
            </div>
          </div>

          <div className="space-y-2.5 mb-5">
            {[
              "10% commission on direct referrals",
              "3% + 2% on level 2 & 3 referrals",
              "26 stage bonuses up to $10,000",
              "Full dashboard & withdrawals",
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <FaCheck className="text-[10px] text-emerald-300" />
                </div>
                <span className="text-sm text-slate-300">{text}</span>
              </div>
            ))}
          </div>

          <Link
            to="/deposit"
            className="flex items-center justify-center w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm shadow-lg shadow-amber-500/20 active:scale-[0.97] transition-transform touch-manipulation"
          >
            <FaWallet className="mr-2" />
            Deposit $50 USDT to Activate
          </Link>
        </div>

        {/* Info cards row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 text-center">
            <FaUser className="text-lg text-cyan-400 mx-auto mb-1.5" />
            <p className="text-[11px] text-slate-400 mb-0.5">Profile</p>
            <p className="text-xs font-semibold text-white truncate">
              {user?.fullName?.split(" ")[0]}
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 text-center">
            <FaLink className="text-lg text-violet-400 mx-auto mb-1.5" />
            <p className="text-[11px] text-slate-400 mb-0.5">Code</p>
            <p className="text-xs font-mono font-bold text-cyan-200 truncate">
              {user?.referralCode}
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 text-center relative">
            <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            <FaDollarSign className="text-lg text-emerald-400 mx-auto mb-1.5" />
            <p className="text-[11px] text-slate-400 mb-0.5">Balance</p>
            <p className="text-xs font-bold text-white">
              ${user?.walletBalance?.toFixed(2) || "0.00"}
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center">
            <FaInfoCircle className="mr-2 text-cyan-400 text-xs" />
            How to Activate
          </h3>
          <div className="space-y-2.5">
            {[
              'Tap "Deposit $50 USDT" above',
              "Copy your BEP20 deposit address",
              "Send $50+ USDT via BSC network",
              "Wait for confirmation (~3 min)",
              "Account activates automatically!",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-cyan-300">
                    {i + 1}
                  </span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ─── ACTIVATED DASHBOARD ─── */
  return (
    <div className="space-y-5 pb-6">
      {/* Greeting + Balance Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500 via-blue-600 to-violet-600 p-5 shadow-xl shadow-cyan-500/10">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <p className="text-cyan-100 text-xs font-medium mb-0.5">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </p>
          <h1 className="text-lg font-bold text-white mb-4">
            Welcome back, {user?.fullName?.split(" ")[0]} 👋
          </h1>

          {/* Balance pill */}
          <div className="bg-white/15 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-cyan-100 font-medium uppercase tracking-wider">
                  Total Balance
                </p>
                <p className="text-2xl font-extrabold text-white tracking-tight mt-0.5">
                  ${user?.walletBalance?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                <FaWallet className="text-lg text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STAT CHIPS – horizontal scroll on mobile */}
      <div className="relative">
        {/* Scroll hint gradient – right edge */}
        <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none rounded-r-2xl" />
        <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-hide">
          {[
            {
              label: "Earnings",
              value: `$${dashboard?.earnings.total.toFixed(2) || "0.00"}`,
              icon: FaChartLine,
              gradient: "from-emerald-500 to-teal-600",
              glow: "shadow-emerald-500/20",
            },
            {
              label: "Stage",
              value: `${dashboard?.user.currentStage || 0}/26`,
              icon: FaTrophy,
              gradient: "from-violet-500 to-purple-600",
              glow: "shadow-violet-500/20",
            },
            {
              label: "Referrals",
              value: dashboard?.referrals.direct || 0,
              icon: FaUsers,
              gradient: "from-sky-500 to-cyan-600",
              glow: "shadow-sky-500/20",
            },
            {
              label: "Below You",
              value: dashboard?.system.usersBelow || 0,
              icon: FaArrowDown,
              gradient: "from-amber-500 to-orange-500",
              glow: "shadow-amber-500/20",
            },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className={`flex-shrink-0 snap-start w-[140px] rounded-2xl bg-gradient-to-br ${stat.gradient} p-3.5 shadow-lg ${stat.glow} relative overflow-hidden`}
              >
                <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/10 blur-lg" />
                <div className="relative z-10">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center mb-2">
                    <Icon className="text-sm text-white" />
                  </div>
                  <p className="text-white/70 text-[11px] font-medium">
                    {stat.label}
                  </p>
                  <p className="text-lg font-extrabold text-white tracking-tight">
                    {stat.value}
                  </p>
                </div>
              </div>
            );
          })}
          {/* Spacer so last card doesn't hug the edge */}
          <div className="flex-shrink-0 w-1" />
        </div>
        {/* Scroll indicator dots */}
        <div className="flex justify-center gap-1.5 mt-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === 0 ? "w-4 bg-cyan-400" : "w-1.5 bg-white/20"
              }`}
            />
          ))}
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center">
          <FaFire className="mr-1.5 text-amber-400 text-xs" />
          Quick Actions
        </h3>
        <div className="grid grid-cols-4 gap-2">
          {[
            ...(user?.isActivated ?
              []
            : [
                {
                  to: "/deposit",
                  icon: FaWallet,
                  label: "Deposit",
                  bg: "bg-cyan-500/15",
                  color: "text-cyan-300",
                },
              ]),
            {
              to: "/withdraw",
              icon: FaMoneyBillWave,
              label: "Withdraw",
              bg: "bg-emerald-500/15",
              color: "text-emerald-300",
            },
            {
              to: "/referrals",
              icon: FaUsers,
              label: "Referrals",
              bg: "bg-violet-500/15",
              color: "text-violet-300",
            },
            {
              to: "/stages",
              icon: FaTrophy,
              label: "Stages",
              bg: "bg-amber-500/15",
              color: "text-amber-300",
            },
            {
              to: "/profile",
              icon: FaShieldAlt,
              label: "Security",
              bg: "bg-rose-500/15",
              color: "text-rose-300",
            },
          ].map((action, i) => (
            <Link
              key={i}
              to={action.to}
              className="flex flex-col items-center py-3 rounded-xl active:bg-white/[0.06] transition touch-manipulation"
            >
              <div
                className={`w-11 h-11 ${action.bg} rounded-xl flex items-center justify-center mb-1.5`}
              >
                <action.icon className={`text-base ${action.color}`} />
              </div>
              <span className="text-[11px] font-medium text-slate-300">
                {action.label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* REFERRAL LINK BANNER */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-600 to-violet-600 p-4 shadow-lg shadow-violet-500/10">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FaLink className="text-sm text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Invite & Earn</h3>
              <p className="text-[11px] text-cyan-100">10% on every referral</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              value={`${window.location.origin}/register?ref=${user?.referralCode}`}
              readOnly
              className="flex-1 min-w-0 px-3 py-2.5 rounded-lg text-[11px] font-mono bg-black/30 text-white/80 border border-white/10 truncate"
            />
            <button
              onClick={copyReferralLink}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-white rounded-lg text-slate-900 font-semibold text-xs active:scale-95 transition-transform touch-manipulation shadow-md"
            >
              <FaCopy className="text-[10px]" />
              Copy
            </button>
          </div>
          <p className="text-[10px] text-cyan-100/70 mt-2">
            Code:{" "}
            <span className="font-bold text-white">{user?.referralCode}</span>
            {" · "}
            {dashboard?.referrals.direct || 0} joined
          </p>
        </div>
      </div>

      {/* EARNINGS BREAKDOWN */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white flex items-center">
            <FaChartLine className="mr-1.5 text-cyan-400 text-xs" />
            Earnings
          </h3>
          <span className="text-[10px] text-slate-400 bg-white/[0.06] px-2.5 py-1 rounded-full">
            All time
          </span>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-slate-400">Referral Earnings</span>
              <span className="text-sm font-bold text-emerald-300">
                ${dashboard?.earnings.referral.toFixed(2) || "0.00"}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-700"
                style={{
                  width: `${Math.min(
                    (dashboard?.earnings.referral /
                      (dashboard?.earnings.total || 1)) *
                      100,
                    100,
                  )}%`,
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-slate-400">Stage Earnings</span>
              <span className="text-sm font-bold text-cyan-300">
                ${dashboard?.earnings.stage.toFixed(2) || "0.00"}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-400 transition-all duration-700"
                style={{
                  width: `${Math.min(
                    (dashboard?.earnings.stage /
                      (dashboard?.earnings.total || 1)) *
                      100,
                    100,
                  )}%`,
                }}
              />
            </div>
          </div>

          <div className="pt-3 border-t border-white/[0.06] flex justify-between items-center">
            <span className="text-xs font-semibold text-white">
              Total Earnings
            </span>
            <span className="text-lg font-extrabold bg-gradient-to-r from-cyan-300 to-violet-400 bg-clip-text text-transparent">
              ${dashboard?.earnings.total.toFixed(2) || "0.00"}
            </span>
          </div>
        </div>
      </div>

      {/* STATS LIST */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center">
          <FaStar className="mr-1.5 text-amber-400 text-xs" />
          Your Stats
        </h3>
        <div className="space-y-2">
          {[
            {
              icon: FaUsers,
              bg: "bg-violet-500/15",
              color: "text-violet-300",
              label: "Active Members",
              value: dashboard?.system.totalActiveMembers || 0,
            },
            {
              icon: FaChartLine,
              bg: "bg-cyan-500/15",
              color: "text-cyan-300",
              label: "Chain Position",
              value: `#${dashboard?.user.positionInChain}`,
            },
            {
              icon: FaArrowDown,
              bg: "bg-emerald-500/15",
              color: "text-emerald-300",
              label: "Users Below",
              value: dashboard?.system.usersBelow || 0,
              sub: "Stage qualification",
            },
            {
              icon: FaClock,
              bg: "bg-amber-500/15",
              color: "text-amber-300",
              label: "Member Since",
              value: new Date(user?.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              }),
            },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.04]"
              >
                <div
                  className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`text-sm ${item.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">{item.label}</p>
                  <p className="text-sm font-bold text-white">{item.value}</p>
                  {item.sub && (
                    <p className="text-[10px] text-emerald-400 mt-0.5">
                      {item.sub}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RECENT REFERRALS */}
      {dashboard?.referrals.list && dashboard.referrals.list.length > 0 && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center">
              <FaUser className="mr-1.5 text-violet-400 text-xs" />
              Recent Referrals
            </h3>
            <Link
              to="/referrals"
              className="text-[11px] text-cyan-300 font-medium flex items-center gap-1"
            >
              All <FaChevronRight className="text-[8px]" />
            </Link>
          </div>
          <div className="space-y-2">
            {dashboard.referrals.list.slice(0, 5).map((ref) => (
              <div
                key={ref._id}
                className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.04]"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {ref.fullName?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white truncate">
                    {ref.fullName}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    @{ref.username} ·{" "}
                    {new Date(ref.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                      ref.isActivated ?
                        "bg-emerald-500/15 text-emerald-300"
                      : "bg-amber-500/15 text-amber-300"
                    }`}
                  >
                    {ref.isActivated ? "Active" : "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RECENT ACTIVITY */}
      {dashboard?.recentTransactions &&
        dashboard.recentTransactions.length > 0 && (
          <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white flex items-center">
                <FaClock className="mr-1.5 text-cyan-400 text-xs" />
                Recent Activity
              </h3>
              <Link
                to="/transactions"
                className="text-[11px] text-cyan-300 font-medium flex items-center gap-1"
              >
                All <FaChevronRight className="text-[8px]" />
              </Link>
            </div>
            <div className="space-y-2">
              {dashboard.recentTransactions.slice(0, 5).map((tx) => (
                <div
                  key={tx._id}
                  className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.04]"
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      tx.amount >= 0 ? "bg-emerald-500/15" : "bg-rose-500/15"
                    }`}
                  >
                    {tx.amount >= 0 ?
                      <FaArrowDown className="text-xs text-emerald-300" />
                    : <FaArrowUp className="text-xs text-rose-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">
                      {tx.description}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {new Date(tx.createdAt).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <span
                    className={`text-sm font-bold flex-shrink-0 ${
                      tx.amount >= 0 ? "text-emerald-300" : "text-rose-300"
                    }`}
                  >
                    {tx.amount >= 0 ? "+" : ""}${Math.abs(tx.amount).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default Dashboard;
